import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@dukene/shared';
import {
  CreditCard,
  Search,
  Eye,
  Edit2,
  X,
  CheckCircle,
  RefreshCw,
  Zap,
  Calculator,
  Building,
  Gift,
  Wallet,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';

interface Tenant {
  id: string;
  name: string;
  email: string;
  ownerName?: string;
  ownerEmail?: string;
  plan: 'BASIC' | 'PRO' | 'MAX' | 'ENTERPRISE';
  storeCount: number;
  walletCount?: number;
  walletBalance?: number;
  joinedAt: string;
}

interface Transaction {
  id: string;
  type: string;
  amount: number;
  status: string;
  description: string;
  date: string;
}

interface TenantsResponse {
  items: Tenant[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasMore: boolean;
  };
}

interface TransactionsResponse {
  items: Transaction[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasMore: boolean;
  };
}

interface PayHeroFeeBand {
  amountFrom: number;
  amountTo: number;
  price: number;
}

interface PayHeroPricingResponse {
  fees: PayHeroFeeBand[];
  provider: string;
  source: string;
  lastUpdated?: string;
}

const BONUS_PRESETS = [200, 500, 1000, 2500, 5000];

export default function Payments() {
  const [activeMainTab, setActiveMainTab] = useState<'tenants' | 'payhero'>('tenants');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [newPlan, setNewPlan] = useState<string>('BASIC');

  // Bonus modal state
  const [isBonusModalOpen, setIsBonusModalOpen] = useState(false);
  const [bonusAmount, setBonusAmount] = useState<number | ''>(500);
  const [bonusReason, setBonusReason] = useState<string>('Bonus');
  const [bonusSuccess, setBonusSuccess] = useState(false);

  // PayHero tab state
  const [calcAmount, setCalcAmount] = useState<string>('1500');
  const [isRefreshingPayhero, setIsRefreshingPayhero] = useState(false);

  const queryClient = useQueryClient();

  // Fetch tenants
  const { data: tenantsData, isLoading: isLoadingTenants } = useQuery<TenantsResponse>({
    queryKey: ['admin-tenants', currentPage, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      if (search) params.append('search', search);

      return apiClient.get<TenantsResponse>(`/admin/billing/tenants?${params}`);
    },
    enabled: activeMainTab === 'tenants',
  });

  // Fetch selected tenant transactions
  const { data: transactionsData, isLoading: isLoadingTransactions } = useQuery<TransactionsResponse>({
    queryKey: ['admin-tenant-transactions', selectedTenant?.id],
    queryFn: async () => {
      if (!selectedTenant) return { items: [], meta: { total: 0, page: 1, limit: 20, pages: 1, hasMore: false } };
      return apiClient.get<TransactionsResponse>(`/admin/billing/tenants/${selectedTenant.id}/transactions`);
    },
    enabled: !!selectedTenant && isHistoryModalOpen,
  });

  // Fetch PayHero wholesale fees
  const {
    data: payheroData,
    isLoading: isLoadingPayhero,
    isFetching: isFetchingPayhero,
  } = useQuery<PayHeroPricingResponse>({
    queryKey: ['admin-payhero-fees'],
    queryFn: async () => {
      return apiClient.get<PayHeroPricingResponse>('/admin/billing/payhero-fees');
    },
    enabled: activeMainTab === 'payhero',
    staleTime: 60 * 60 * 1000,
  });

  const refreshPayheroPricing = async () => {
    setIsRefreshingPayhero(true);
    try {
      const fresh = await apiClient.get<PayHeroPricingResponse>('/admin/billing/payhero-fees?refresh=true');
      queryClient.setQueryData(['admin-payhero-fees'], fresh);
    } catch (err) {
      console.error('Failed to refresh PayHero fees:', err);
    } finally {
      setIsRefreshingPayhero(false);
    }
  };

  // Change Plan Mutation
  const changePlanMutation = useMutation({
    mutationFn: async (plan: string) => {
      if (!selectedTenant) throw new Error('No tenant selected');
      return apiClient.patch(`/admin/billing/tenants/${selectedTenant.id}/plan`, { plan });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      setIsPlanModalOpen(false);
      setSelectedTenant(null);
    },
  });

  // Grant Credit Bonus Mutation
  const grantBonusMutation = useMutation({
    mutationFn: async (data: { accountId: string; amount: number; reason: string }) => {
      return apiClient.post(`/admin/billing/tenants/${data.accountId}/grant-bonus`, {
        amount: data.amount,
        reason: data.reason,
      });
    },
    onSuccess: () => {
      setBonusSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      setTimeout(() => {
        setBonusSuccess(false);
        setIsBonusModalOpen(false);
        setSelectedTenant(null);
      }, 1500);
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleOpenHistory = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsHistoryModalOpen(true);
  };

  const handleOpenPlanModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setNewPlan(tenant.plan);
    setIsPlanModalOpen(true);
  };

  const handleOpenBonusModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setBonusAmount(500);
    setBonusReason('Bonus');
    setIsBonusModalOpen(true);
  };

  const handleSavePlan = () => {
    if (window.confirm(`Are you sure you want to change the plan to ${newPlan}?`)) {
      changePlanMutation.mutate(newPlan);
    }
  };

  // PayHero fee calculator lookup
  const payheroFees = payheroData?.fees || [];
  const numCalcAmount = Number(calcAmount);
  const selectedPayheroFee = useMemo(
    () =>
      Number.isFinite(numCalcAmount) && numCalcAmount > 0
        ? payheroFees.find((fee) => numCalcAmount >= fee.amountFrom && numCalcAmount <= fee.amountTo)
        : undefined,
    [payheroFees, numCalcAmount]
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-black">Billing & Gateway Pricing</h1>
          <p className="text-gray-500 mt-1">Manage tenant subscriptions, assign credit bonuses, and monitor PayHero M-PESA wholesale rates</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setActiveMainTab('tenants')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
              activeMainTab === 'tenants'
                ? 'bg-white text-black shadow-sm'
                : 'text-gray-600 hover:text-black'
            }`}
          >
            <Building className="w-4 h-4" />
            Tenant Subscriptions
          </button>
          <button
            type="button"
            onClick={() => setActiveMainTab('payhero')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
              activeMainTab === 'payhero'
                ? 'bg-white text-black shadow-sm'
                : 'text-gray-600 hover:text-black'
            }`}
          >
            <Zap className="w-4 h-4 text-emerald-600" />
            PayHero Wholesale Rates
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: TENANT SUBSCRIPTIONS */}
      {/* ========================================================================= */}
      {activeMainTab === 'tenants' && (
        <div className="space-y-6">
          {/* Filters & Search */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="text-xs text-gray-500 font-medium">
              {tenantsData?.meta?.total || 0} total merchant accounts
            </div>
          </div>

          {/* Tenants Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {isLoadingTenants ? (
              <div className="p-12 text-center text-gray-500">Loading tenant billing accounts...</div>
            ) : !tenantsData || tenantsData.items.length === 0 ? (
              <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                <CreditCard className="h-12 w-12 text-gray-300 mb-4" />
                <p>No tenants found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tenant</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Current Plan</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Credits Wallet</th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Stores</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tenantsData.items.map((tenant) => (
                      <tr key={tenant.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-bold text-gray-900">{tenant.name}</div>
                            <div className="text-xs text-gray-500">{tenant.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full ${
                            tenant.plan === 'ENTERPRISE' ? 'bg-purple-100 text-purple-800' :
                            tenant.plan === 'MAX' ? 'bg-indigo-100 text-indigo-800' :
                            tenant.plan === 'PRO' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {tenant.plan}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                            <span className="text-sm font-bold text-emerald-700">
                              {formatCurrency(tenant.walletBalance ?? 200)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 font-semibold">
                          {tenant.storeCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(tenant.joinedAt), 'MMM dd, yyyy')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleOpenBonusModal(tenant)}
                              className="text-emerald-700 hover:text-emerald-900 flex items-center gap-1 transition-colors bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                              title="Grant Credit Bonus"
                            >
                              <Gift className="h-3.5 w-3.5" />
                              <span>Bonus</span>
                            </button>
                            <button
                              onClick={() => handleOpenHistory(tenant)}
                              className="text-gray-600 hover:text-black flex items-center gap-1 transition-colors bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                              title="View Billing History"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              <span>History</span>
                            </button>
                            <button
                              onClick={() => handleOpenPlanModal(tenant)}
                              className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1 transition-colors bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                              title="Change Plan"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                              <span>Plan</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: PAYHERO WHOLESALE GATEWAY RATES */}
      {/* ========================================================================= */}
      {activeMainTab === 'payhero' && (
        <div className="space-y-6 animate-in fade-in duration-200">
          {/* PayHero Overview Banner */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800">
                  LIVE GATEWAY TARIFFS
                </span>
                <span className="text-xs text-gray-500 font-semibold">
                  Provider: PayHero Kenya
                </span>
              </div>
              <h2 className="text-2xl font-black text-black">PayHero M-PESA Wholesale Fee Bands</h2>
              <p className="text-xs text-gray-500 max-w-xl">
                These are the underlying wholesale gateway fees charged by PayHero for M-PESA processing. Platform merchant transaction fees (6% Basic, 3% Pro, 2% Max, 0% Enterprise) are charged separately to merchants via their Credits Wallets.
              </p>
            </div>

            <button
              type="button"
              onClick={refreshPayheroPricing}
              disabled={isFetchingPayhero || isRefreshingPayhero}
              className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-xs font-bold text-black disabled:opacity-50 inline-flex items-center gap-2 shadow-2xs self-start md:self-auto"
            >
              <RefreshCw className={`w-4 h-4 ${isFetchingPayhero || isRefreshingPayhero ? 'animate-spin' : ''}`} />
              Refresh from PayHero
            </button>
          </div>

          {/* PayHero Fee Simulator & Bands Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calculator */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                <Calculator className="w-5 h-5 text-emerald-600" />
                <h3 className="text-sm font-bold text-black">Gateway Cost Simulator</h3>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                  Transaction Amount (KES)
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                    KES
                  </span>
                  <input
                    type="number"
                    min="1"
                    step="50"
                    value={calcAmount}
                    onChange={(e) => setCalcAmount(e.target.value)}
                    placeholder="1500"
                    className="w-full h-11 pl-12 pr-4 rounded-xl border border-gray-300 bg-gray-50 text-base font-bold text-black focus:bg-white focus:outline-none focus:ring-2 focus:ring-black"
                  />
                </div>
              </div>

              {calcAmount && (
                <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-1">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase">PayHero Gateway Wholesale Cost</span>
                  {selectedPayheroFee ? (
                    <p className="text-2xl font-black text-emerald-600">
                      {formatCurrency(selectedPayheroFee.price)}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 font-semibold">
                      No matching tariff band for this amount.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Bands Table */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-black">Active Wholesale Fee Bands</h3>
                <span className="text-xs text-gray-500 font-medium">
                  {payheroFees.length} tariff tiers
                </span>
              </div>

              {isLoadingPayhero ? (
                <div className="p-12 text-center text-gray-500">Loading PayHero fee bands...</div>
              ) : payheroFees.length === 0 ? (
                <div className="p-12 text-center text-gray-500">No fee bands retrieved. Click Refresh to load.</div>
              ) : (
                <div className="overflow-x-auto max-h-96 custom-scrollbar">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-500 font-bold sticky top-0">
                      <tr>
                        <th className="text-left px-5 py-3 uppercase tracking-wider">Amount From</th>
                        <th className="text-left px-5 py-3 uppercase tracking-wider">Amount To</th>
                        <th className="text-right px-5 py-3 uppercase tracking-wider">PayHero Wholesale Fee</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payheroFees.map((fee) => (
                        <tr key={`${fee.amountFrom}-${fee.amountTo}`} className="hover:bg-gray-50">
                          <td className="px-5 py-3 font-semibold text-gray-900">{formatCurrency(fee.amountFrom)}</td>
                          <td className="px-5 py-3 font-semibold text-gray-900">{formatCurrency(fee.amountTo)}</td>
                          <td className="px-5 py-3 text-right font-black text-emerald-600">
                            {formatCurrency(fee.price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODALS */}
      {/* ========================================================================= */}

      {/* 1. Grant Credit Bonus Modal */}
      {isBonusModalOpen && selectedTenant && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsBonusModalOpen(false)} />
            
            <div className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white rounded-2xl shadow-2xl space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center">
                    <Gift className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Grant Credit Bonus</h3>
                    <p className="text-xs text-gray-500">{selectedTenant.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsBonusModalOpen(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {bonusSuccess ? (
                <div className="py-8 flex flex-col items-center justify-center text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center animate-bounce">
                    <CheckCircle className="w-7 h-7" />
                  </div>
                  <h4 className="text-base font-bold text-gray-900">Bonus Credits Awarded!</h4>
                  <p className="text-xs text-gray-500">The organisation wallet has been credited and the owner notified.</p>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!bonusAmount || Number(bonusAmount) <= 0) return;
                    grantBonusMutation.mutate({
                      accountId: selectedTenant.id,
                      amount: Number(bonusAmount),
                      reason: bonusReason,
                    });
                  }}
                  className="space-y-4"
                >
                  {/* Current Balance */}
                  <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between text-xs">
                    <span className="text-gray-500 font-medium">Current Organisation Balance:</span>
                    <strong className="text-emerald-700 font-bold">
                      {formatCurrency(selectedTenant.walletBalance ?? 200)}
                    </strong>
                  </div>

                  {/* Preset Amount Grid */}
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1.5">
                      Select Bonus Amount (KES)
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {BONUS_PRESETS.map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setBonusAmount(amt)}
                          className={`py-2 rounded-lg text-xs font-bold border transition-all ${
                            bonusAmount === amt
                              ? 'bg-black text-white border-black shadow-sm'
                              : 'bg-white text-gray-800 border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          +{amt.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount */}
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1">
                      Custom Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                        KES
                      </span>
                      <input
                        type="number"
                        min="10"
                        step="50"
                        value={bonusAmount}
                        onChange={(e) => setBonusAmount(e.target.value ? Number(e.target.value) : '')}
                        placeholder="500"
                        className="w-full h-10 pl-12 pr-3 rounded-lg border border-gray-300 text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-black"
                        required
                      />
                    </div>
                  </div>

                  {/* Tag / Memo */}
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1">
                      Tag / Description
                    </label>
                    <input
                      type="text"
                      value={bonusReason}
                      onChange={(e) => setBonusReason(e.target.value)}
                      placeholder="Bonus"
                      className="w-full h-10 px-3 rounded-lg border border-gray-300 text-xs font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>

                  <div className="pt-2 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setIsBonusModalOpen(false)}
                      className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-black"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={grantBonusMutation.isPending || !bonusAmount || Number(bonusAmount) <= 0}
                      className="px-5 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                    >
                      {grantBonusMutation.isPending ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Awarding...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5" />
                          Award {bonusAmount ? formatCurrency(Number(bonusAmount)) : ''} Credits
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2. History Modal */}
      {isHistoryModalOpen && selectedTenant && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsHistoryModalOpen(false)} />
            
            <div className="relative inline-block w-full max-w-2xl p-6 overflow-hidden text-left align-middle transition-all transform bg-white rounded-2xl shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Billing History</h3>
                  <p className="text-xs text-gray-500 mt-1">{selectedTenant.name} ({selectedTenant.email})</p>
                </div>
                <button
                  onClick={() => setIsHistoryModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {isLoadingTransactions ? (
                <div className="py-12 text-center text-gray-500">Loading transactions...</div>
              ) : !transactionsData || transactionsData.items.length === 0 ? (
                <div className="py-12 text-center text-gray-500">No billing history found for this tenant.</div>
              ) : (
                <div className="max-h-96 overflow-y-auto custom-scrollbar">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Description</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">Amount</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 text-sm">
                      {transactionsData.items.map((tx) => (
                        <tr key={tx.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {format(new Date(tx.date), 'MMM dd, yyyy')}
                          </td>
                          <td className="px-4 py-3 text-xs font-medium text-gray-900">{tx.description}</td>
                          <td className="px-4 py-3 text-xs font-bold text-right text-gray-900">{formatCurrency(tx.amount)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                              tx.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {tx.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 3. Change Plan Modal */}
      {isPlanModalOpen && selectedTenant && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsPlanModalOpen(false)} />
            
            <div className="relative inline-block w-full max-w-md p-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Change Plan</h3>
              <p className="text-sm text-gray-500 mb-6">Select a new billing plan for {selectedTenant.name}.</p>
              
              <div className="space-y-4 mb-8">
                {['BASIC', 'PRO', 'MAX', 'ENTERPRISE'].map((planOption) => (
                  <label 
                    key={planOption} 
                    className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${
                      newPlan === planOption ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value={planOption}
                      checked={newPlan === planOption}
                      onChange={(e) => setNewPlan(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <div className="ml-4 flex-1">
                      <span className="block text-sm font-bold text-gray-900">{planOption}</span>
                      {selectedTenant.plan === planOption && (
                        <span className="block text-xs text-gray-500 mt-0.5">Current Plan</span>
                      )}
                    </div>
                    {newPlan === planOption && (
                      <CheckCircle className="h-5 w-5 text-indigo-600" />
                    )}
                  </label>
                ))}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsPlanModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={changePlanMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePlan}
                  disabled={changePlanMutation.isPending || newPlan === selectedTenant.plan}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {changePlanMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
